// InterviewGamePlanDetail.jsx — Left TOC + scroll-spy + RIGHT Sponsored
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

/* ================== AdSense helpers ================== */
const ADSENSE_CLIENT = "ca-pub-9033879555371063";
const ADS_ENABLED = (process.env.REACT_APP_ENABLE_ADS ?? "true") === "true";

function useAdSense(clientId, enabled) {
  useEffect(() => {
    if (!enabled || !clientId) return;

    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    } else {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {
        /* ignore */
      }
    }
  }, [clientId, enabled]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      /* ignore */
    }
  }, [enabled]);

  if (!enabled) return null;

  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

function RightSponsoredCard() {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
          <h3 className="h6 text-uppercase text-muted mb-0">Sponsored</h3>
          <span className="badge rounded-pill text-bg-light border text-muted">Ads</span>
        </div>
        <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

/* ================== small helpers ================== */
const Section = ({ id, title, children }) => (
  <section id={id} className="mb-5">
    <div className="d-flex align-items-start justify-content-between gap-2">
      <h2 className="h4 mb-2">{title}</h2>
    </div>
    {children}
    <div className="mt-3">
      <a href="#top" className="small text-muted">
        ↑ Back to top
      </a>
    </div>
  </section>
);

const Say = ({ children }) => (
  <div className="alert alert-primary d-flex align-items-start gap-2 py-2 mb-3" role="note">
    <span className="badge text-bg-primary">Say</span>
    <div className="mb-0">{children}</div>
  </div>
);

const TagPill = ({ children }) => (
  <span className="badge rounded-pill text-bg-light border text-muted fw-semibold">{children}</span>
);

const Panel = ({ title, tag, children }) => (
  <div className="card border-0 shadow-sm mt-3">
    <div className="card-body">
      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
        <div className="fw-semibold">{title}</div>
        {tag ? <TagPill>{tag}</TagPill> : null}
      </div>
      {children}
    </div>
  </div>
);

const BulletList = ({ items }) => (
  <ul className="mb-0">
    {items.map((it, idx) => (
      <li key={idx} className={idx === items.length - 1 ? "" : "mb-3"}>
        <div>{it.main}</div>
        {it.detail ? <div className="medium text-muted mt-1">{it.detail}</div> : null}
      </li>
    ))}
  </ul>
);

const TOC = [
  { id: "clarify", label: "1) Clarify goals & constraints" },
  { id: "sizing", label: "2) Back-of-the-envelope sizing" },
  { id: "api-model", label: "3) API & data model first" },
  { id: "arch", label: "4) High-level architecture" },
  { id: "scale", label: "5) Scale & resilience plan" },
  { id: "consistency", label: "6) Consistency vs availability" },
  { id: "security", label: "7) Security & privacy" },
  { id: "observability", label: "8) Observability & rollouts" },
  { id: "tradeoffs", label: "9) Trade-offs & alternatives" },
];

/* ================== main page ================== */
export default function InterviewGamePlanDetail() {
  useAdSense(ADSENSE_CLIENT, ADS_ENABLED);

  const [activeId, setActiveId] = useState(TOC[0].id);

  // scroll-spy
  useEffect(() => {
    const opts = { root: null, rootMargin: "0px 0px -60% 0px", threshold: 0.1 };
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActiveId(entry.target.id);
      });
    }, opts);

    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });

    return () => obs.disconnect();
  }, []);

  // Running example assumptions (Facebook-like Home Feed). Keep these consistent across sections.
  const fb = useMemo(() => {
    const MAU = 500_000_000;
    const DAU = 200_000_000;
    const feedOpensPerUserPerDay = 8; // average
    const feedReadsPerDay = DAU * feedOpensPerUserPerDay; // 1.6B
    const avgFeedRps = feedReadsPerDay / 86400; // ~18.5k
    const peakMultiplier = 6;
    const peakFeedRps = avgFeedRps * peakMultiplier; // ~111k

    const postsPerDay = 20_000_000; // made-up but plausible for scale math
    const avgPostRps = postsPerDay / 86400; // ~231
    const peakPostRps = avgPostRps * 5; // burstier creation

    const feedPagePosts = 30;
    const feedPayloadKbCompressed = 25; // metadata + text + URLs, NOT media bytes (served via CDN)
    const peakEgressGbps = (peakFeedRps * feedPayloadKbCompressed * 1024) / (1024 ** 3); // approx GB/s

    const avgFollowers = 300; // for fanout intuition (distribution is heavy-tailed)
    const normalFanoutEntriesPerDay = postsPerDay * avgFollowers; // huge, motivates hybrid
    const feedEntryBytes = 64; // (postId, authorId, ts, score, flags, overhead)
    const feedEntriesGbPerDay = (normalFanoutEntriesPerDay * feedEntryBytes) / (1024 ** 3);

    return {
      MAU,
      DAU,
      feedOpensPerUserPerDay,
      feedReadsPerDay,
      avgFeedRps,
      peakMultiplier,
      peakFeedRps,
      postsPerDay,
      avgPostRps,
      peakPostRps,
      feedPagePosts,
      feedPayloadKbCompressed,
      peakEgressGbps,
      avgFollowers,
      feedEntriesGbPerDay,
    };
  }, []);

  return (
    <div className="container-lg py-4" id="top">
      {/* breadcrumb */}
      <nav className="small mb-2">
        <Link to="/systemDesign">System Design</Link>
        <span className="text-muted"> / </span>
        <span>Interview game plan</span>
      </nav>

      {/* header */}
      <header
        className="mb-4 text-center rounded-3 p-3 p-md-4"
        style={{
          background: "linear-gradient(135deg, rgba(13,110,253,.08), rgba(102,16,242,.06))",
        }}
      >
        <div>
          <span className="badge bg-dark text-white mb-2">System Design</span>
          <h1 className="h3 mb-1">1) Interview game plan — detailed guide</h1>
          <p className="text-muted mb-0" style={{ maxWidth: 900, margin: "0 auto" }}>
            A reusable template for <em>any</em> system design interview, plus a running example:{" "}
            <strong>Facebook-like Home Feed</strong> (posts + ranking + fanout).
          </p>
        </div>
      </header>

      {/* content: LEFT TOC + MAIN + RIGHT Sponsored */}
      <div className="row g-4">
        {/* LEFT: On this page */}
        <aside className="col-12 col-xl-2 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                  <h3 className="h6 text-uppercase text-muted mb-0">On this page</h3>
                  <TagPill>TOC</TagPill>
                </div>

                <nav className="nav flex-column small">
                  {TOC.map(({ id: tocId, label }) => (
                    <a
                      key={tocId}
                      href={`#${tocId}`}
                      className={`nav-link px-0 ${activeId === tocId ? "fw-semibold text-primary" : ""}`}
                      aria-current={activeId === tocId ? "true" : undefined}
                    >
                      {label}
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            <div className="d-none d-xl-block small text-muted mt-2">Tip: Use “Print” to save as PDF.</div>
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7">
          {/* 1 */}
          <Section id="clarify" title="1) Clarify goals & constraints">
            <Say>“Before I design, I’ll confirm goals, scale, and constraints so I optimize for the right thing.”</Say>

            <Panel title="Checklist" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Users &amp; use cases:</strong> Who are the primary users? Real-time UX or batch?
                        Mobile/web/3P API?
                      </>
                    ),
                    detail:
                      "Clarify the “happy path” first (core journey), then edge cases (privacy, abuse, cold start, failures). Ask what must be real-time vs can be async.",
                  },
                  {
                    main: (
                      <>
                        <strong>Scale numbers:</strong> DAU/MAU, QPS (reads/writes), concurrency, expected growth rate.
                      </>
                    ),
                    detail:
                      "Ask for read/write ratio, burstiness (peak multiplier), geo distribution, and whether traffic is “spiky” (events) or “smooth” (B2B).",
                  },
                  {
                    main: (
                      <>
                        <strong>SLOs:</strong> Availability target (e.g., 99.9% monthly), latency budgets (p95/p99 per
                        critical endpoint).
                      </>
                    ),
                    detail:
                      "Identify the few endpoints that matter most (e.g., feed read, post write). Agree on p95/p99 and error budgets early to drive trade-offs.",
                  },
                  {
                    main: (
                      <>
                        <strong>Data guarantees:</strong> Durability requirements, read-your-writes vs eventual consistency.
                      </>
                    ),
                    detail:
                      "Decide what must be strong (money/privacy/auth) and what can be bounded-stale (counts/feeds/search). Define acceptable staleness windows.",
                  },
                  {
                    main: (
                      <>
                        <strong>Retention &amp; deletion:</strong> Data retention windows, legal holds, regional residency.
                      </>
                    ),
                    detail:
                      "Call out RTBF (Right-to-be-Forgotten) scope: DB, cache, search, analytics, backups. Confirm TTLs and compliance constraints.",
                  },
                  {
                    main: (
                      <>
                        <strong>Topology:</strong> Single region vs multi-region, active-active vs active-passive.
                      </>
                    ),
                    detail:
                      "Tie topology to user latency + availability needs. Start simple when allowed; evolve when the business requires cross-region resilience or global latency.",
                  },
                  {
                    main: (
                      <>
                        <strong>Budget &amp; team reality:</strong> Managed services ok? Open-source + ops? Time to MVP?
                      </>
                    ),
                    detail:
                      "Pick the simplest architecture that meets requirements. Explicitly state what you’ll defer (multi-region, advanced ranking, complex search).",
                  },
                ]}
              />
            </Panel>

            <Panel title="Facebook-like Home Feed — clarified goals & constraints" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Users &amp; use cases:</strong> Personalized Home Feed for logged-in users (scroll, refresh,
                        infinite pagination). Users can post, like, comment, follow/unfollow.
                      </>
                    ),
                    detail:
                      "Core UX: feed load + scroll. Secondary: posting and interactions. Ranking can be async/approx; privacy/blocklist must be correct.",
                  },
                  {
                    main: (
                      <>
                        <strong>Scale numbers:</strong> ~{(fb.MAU / 1e6).toFixed(0)}M MAU, ~{(fb.DAU / 1e6).toFixed(0)}M DAU.
                        Avg feed opens {fb.feedOpensPerUserPerDay}×/day → {(fb.feedReadsPerDay / 1e9).toFixed(1)}B reads/day.
                      </>
                    ),
                    detail: `Avg ≈ ${Math.round(fb.avgFeedRps).toLocaleString()} RPS; peak (×${fb.peakMultiplier}) ≈ ${Math.round(
                      fb.peakFeedRps
                    ).toLocaleString()} RPS. Posts: ~${(fb.postsPerDay / 1e6).toFixed(
                      0
                    )}M/day → avg ~${Math.round(fb.avgPostRps).toLocaleString()} RPS; peak ~${Math.round(
                      fb.peakPostRps
                    ).toLocaleString()} RPS.`,
                  },
                  {
                    main: (
                      <>
                        <strong>SLOs:</strong> Feed read p95 ≤ 200ms (server) / p99 ≤ 400ms; Post create p95 ≤ 300ms for “acknowledged”.
                      </>
                    ),
                    detail:
                      "We optimize for read availability and tail latency. Degrade gracefully: fall back to recency feed if ranking pipeline is unhealthy.",
                  },
                  {
                    main: (
                      <>
                        <strong>Data guarantees:</strong> Post durability once acknowledged; privacy/block rules apply immediately; counters (likes/comments) can be eventual (seconds).
                      </>
                    ),
                    detail:
                      "Read-your-writes for author immediately after posting; followers see within a bounded staleness window (e.g., ≤ 5 seconds).",
                  },
                  {
                    main: (
                      <>
                        <strong>Retention &amp; deletion:</strong> Posts stored long-term; raw logs 30 days; derived features 90 days; RTBF supported across storage/search/analytics.
                      </>
                    ),
                    detail:
                      "Media stored in object store with lifecycle policies; deletion events fan out to caches, search indexes, and analytics sinks.",
                  },
                  {
                    main: (
                      <>
                        <strong>Topology:</strong> Start single-region write + Multi-AZ; global CDN for media; add read replicas/regions later.
                      </>
                    ),
                    detail:
                      "Feed is personalized (hard to cache at CDN), so we focus on efficient read path + regional affinity; multi-region primarily for DR.",
                  },
                  {
                    main: (
                      <>
                        <strong>Budget &amp; team reality:</strong> Use managed queue/stream + managed KV/doc store where possible; ship MVP with recency ranking first.
                      </>
                    ),
                    detail:
                      "Phase 1: recency + basic relevance. Phase 2: ML ranking, advanced recommendations, stronger multi-region strategy.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* 2 */}
          <Section id="sizing" title="2) Back-of-the-envelope sizing">
            <Say>“I’ll sanity-check capacity to choose storage, cache size, and network limits.”</Say>

            <Panel title="Checklist" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Throughput:</strong> RPS and fan-out (e.g., feed fan-out to 200 followers).
                      </>
                    ),
                    detail:
                      "Compute average vs peak, then determine whether you can do work synchronously or need queues/workers. Identify hot-key skew.",
                  },
                  {
                    main: (
                      <>
                        <strong>Bandwidth:</strong> avg/peak MB/s; where to compress or push to CDN.
                      </>
                    ),
                    detail:
                      "Separate metadata bytes (API) from media bytes (CDN/object store). Compression, pagination, and prefetching matter.",
                  },
                  {
                    main: (
                      <>
                        <strong>Storage:</strong> daily ingest, 30/90/365-day totals; hot vs warm vs cold tiers.
                      </>
                    ),
                    detail:
                      "Estimate record sizes + indexes + replication factor. Consider compaction/write amplification for LSM stores.",
                  },
                  {
                    main: (
                      <>
                        <strong>Latency math:</strong> p95/p99 budgets across hops (LB → API → service → DB → cache).
                      </>
                    ),
                    detail:
                      "Assign a latency budget per hop and keep retry/timeout budget within the overall SLO.",
                  },
                  {
                    main: (
                      <>
                        <strong>Little’s Law:</strong> L = λ × W to reason about in-flight work/queue depth.
                      </>
                    ),
                    detail:
                      "Use it to size thread pools, connection pools, and queue workers based on peak λ and target W.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Facebook-like Home Feed — quick sizing" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Throughput:</strong> Feed reads peak ≈ {Math.round(fb.peakFeedRps).toLocaleString()} RPS; post writes peak ≈{" "}
                        {Math.round(fb.peakPostRps).toLocaleString()} RPS.
                      </>
                    ),
                    detail:
                      "Fanout pressure is dominated by follower distribution. Use hybrid: fan-out for normal authors; fan-in (pull/merge) for celebrities.",
                  },
                  {
                    main: (
                      <>
                        <strong>Bandwidth:</strong> Feed payload ~{fb.feedPayloadKbCompressed}KB compressed (no media bytes) → peak API egress ≈{" "}
                        {fb.peakEgressGbps.toFixed(1)} GB/s.
                      </>
                    ),
                    detail:
                      "Media served via CDN/object store (images/video). Keep feed response mostly metadata + URLs; use pagination (cursor) and gzip/brotli.",
                  },
                  {
                    main: (
                      <>
                        <strong>Storage:</strong> Posts ~{(fb.postsPerDay / 1e6).toFixed(0)}M/day. If metadata ~2KB/post → ~40GB/day raw (before indexes/replication).
                      </>
                    ),
                    detail:
                      `If naive fan-out-on-write for all authors: avg followers ~${fb.avgFollowers} ⇒ feed entries/day enormous (≈ ${fb.feedEntriesGbPerDay.toFixed(
                        0
                      )} GB/day at ~64B/entry). This motivates hybrid fanout + caching.`,
                  },
                  {
                    main: (
                      <>
                        <strong>Latency math:</strong> p95 budget 200ms server-side: Edge/LB 10ms + Gateway 10ms + FeedReadSvc 20ms + Redis 5ms + FeedStore 20ms + Post hydration 40ms + buffer 95ms.
                      </>
                    ),
                    detail:
                      "Keep retries bounded: timeouts per hop smaller than parent; hedged reads for tail only after a threshold; fall back to cached/recency on ranking issues.",
                  },
                  {
                    main: (
                      <>
                        <strong>Little’s Law:</strong> At peak λ≈{Math.round(fb.peakFeedRps).toLocaleString()} RPS and W≈0.12s (120ms) ⇒ L≈{" "}
                        {Math.round(fb.peakFeedRps * 0.12).toLocaleString()} in-flight.
                      </>
                    ),
                    detail:
                      "That drives max in-flight requests, pool sizes, and concurrency limits. If one feed-read instance safely handles 400 RPS at target utilization, you’d need ~280 instances for peak (plus headroom/AZ).",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* 3 */}
          <Section id="api-model" title="3) API & data model first">
            <Say>“I’ll define the contract and access patterns before picking components.”</Say>

            <Panel title="Checklist" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>APIs:</strong> REST/gRPC; define idempotent write semantics and pagination.
                      </>
                    ),
                    detail:
                      "Prefer cursor-based pagination for feeds. Make writes idempotent with keys (client-generated request IDs).",
                  },
                  {
                    main: (
                      <>
                        <strong>Critical entities:</strong> e.g., User, Photo, Follow, FeedEntry.
                      </>
                    ),
                    detail:
                      "List the minimum entities and which service owns them. Clarify ownership boundaries to prevent cross-service coupling.",
                  },
                  {
                    main: (
                      <>
                        <strong>Access patterns:</strong> top-N reads, hot writes; secondary indexes required?
                      </>
                    ),
                    detail:
                      "Write down the top 3 reads and top 3 writes and optimize the store and indexes for those—not for hypothetical queries.",
                  },
                  {
                    main: (
                      <>
                        <strong>Indexes/keys:</strong> Partition keys to avoid hot partitions; composite indexes for frequent queries.
                      </>
                    ),
                    detail:
                      "Use time-bucketing and/or salting for hot keys. Ensure pagination is stable (sort key monotonic).",
                  },
                  {
                    main: (
                      <>
                        <strong>Schema evolution:</strong> Avro/Protobuf/JSON with backward-compatible changes.
                      </>
                    ),
                    detail:
                      "Add optional fields with defaults; avoid breaking changes; use versioned envelopes for events; validate compatibility in CI.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Facebook-like Home Feed — API & model" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>APIs:</strong> <code>GET /feed?cursor=&amp;limit=30</code>, <code>POST /posts</code>, <code>POST /follow</code>,{" "}
                        <code>POST /like</code>.
                      </>
                    ),
                    detail:
                      "Feed uses cursor (viewerId + lastSeenScore/ts). Writes use idempotency keys (clientRequestId) to handle retries and duplicate submissions.",
                  },
                  {
                    main: (
                      <>
                        <strong>Critical entities:</strong> User, FollowEdge, Post, Reaction, FeedEntry (read model).
                      </>
                    ),
                    detail:
                      "Keep Post as source of truth. FeedEntry is a derived/indexed representation optimized for fast reads.",
                  },
                  {
                    main: (
                      <>
                        <strong>Access patterns:</strong> (1) Read top-N feed items for viewer, (2) hydrate posts by IDs in batch, (3) write post and publish event, (4) update follow edges.
                      </>
                    ),
                    detail:
                      "Avoid N+1: fetch feedEntry IDs then batch-get posts (and optionally batch-get author profiles).",
                  },
                  {
                    main: (
                      <>
                        <strong>Indexes/keys:</strong> FeedEntry partition by <code>viewerId</code>, sort by <code>(rankScore, createdAt)</code> or <code>(timeBucket, createdAt)</code>.
                      </>
                    ),
                    detail:
                      "Hybrid fanout: normal authors fan out postId into followers’ partitions; celebrity authors stored once and merged at read time (fan-in).",
                  },
                  {
                    main: (
                      <>
                        <strong>Schema evolution:</strong> Events like <code>PostCreated</code>, <code>FollowChanged</code>, <code>ReactionAdded</code> in Avro/Proto with registry compatibility checks.
                      </>
                    ),
                    detail:
                      "Consumers are tolerant readers. Only additive changes while multiple versions coexist during rollout.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* 4 */}
          <Section id="arch" title="4) High-level architecture">
            <Say>“Here’s the request path and the async path; I’ll highlight where we cache and queue.”</Say>

            <Panel title="Checklist" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Clients → Edge:</strong> DNS, CDN/Anycast, TLS termination.
                      </>
                    ),
                    detail:
                      "Edge handles TLS, caching for static/media, and global routing. Personalized APIs usually bypass CDN cache but still benefit from Anycast/TLS offload.",
                  },
                  {
                    main: (
                      <>
                        <strong>API Gateway:</strong> AuthN/AuthZ, rate-limit, routing, request shaping.
                      </>
                    ),
                    detail:
                      "Centralize identity, quotas, and request validation. Attach correlation IDs and enforce timeouts.",
                  },
                  {
                    main: (
                      <>
                        <strong>Services:</strong> split read/write paths; separate feed-write from feed-read.
                      </>
                    ),
                    detail:
                      "Optimize read path for low latency; optimize write path for durability and async work dispatch.",
                  },
                  {
                    main: (
                      <>
                        <strong>Storage:</strong> relational for accounts; doc/KV for feed; object store for images.
                      </>
                    ),
                    detail:
                      "Pick stores by access patterns: transactions and integrity → SQL; large-scale indexed reads → KV/doc/wide-column; large blobs → object store.",
                  },
                  {
                    main: (
                      <>
                        <strong>Caching:</strong> CDN for media; Redis/Memcached for hot keys &amp; list heads.
                      </>
                    ),
                    detail:
                      "Cache the “head” of the feed and recently accessed pages. Use request coalescing and jittered TTLs to avoid stampedes.",
                  },
                  {
                    main: (
                      <>
                        <strong>Async:</strong> Kafka/SQS/PubSub for fan-out, recompute, indexing.
                      </>
                    ),
                    detail:
                      "Use streams/queues to decouple writes from heavy fanout/indexing/ranking. Consumers scale independently.",
                  },
                  {
                    main: (
                      <>
                        <strong>Batch/streaming:</strong> ETL to OLAP for analytics, ranking models.
                      </>
                    ),
                    detail:
                      "Analytics/ML pipelines are separate from OLTP. They produce features/scores that feed back into ranking with controlled staleness.",
                  },
                ]}
              />
            </Panel>

            {/* IMPORTANT: Example now follows the SAME template bullets (same order/labels). */}
            <Panel title="Facebook-like Home Feed — architecture (mapped to the template)" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Clients → Edge:</strong> Mobile/Web → Anycast Edge; media via CDN; API over TLS (HTTP/2).
                      </>
                    ),
                    detail:
                      "CDN caches images/videos aggressively. Feed API responses are personalized, so caching is limited, but edge still improves latency and protects origin.",
                  },
                  {
                    main: (
                      <>
                        <strong>API Gateway:</strong> Validate JWT/session, enforce per-user/app token bucket, attach trace IDs, route to FeedRead or PostWrite.
                      </>
                    ),
                    detail:
                      "Gateway also enforces request budgets (timeouts), blocks abusive clients, and provides consistent error schemas.",
                  },
                  {
                    main: (
                      <>
                        <strong>Services:</strong> FeedReadSvc (low-latency) and PostWriteSvc (durable + async). Separate ReactionSvc (likes/comments) from feed rendering.
                      </>
                    ),
                    detail:
                      "Read path avoids heavy joins; write path emits events and returns quickly after durability.",
                  },
                  {
                    main: (
                      <>
                        <strong>Storage:</strong> PostStore (durable metadata/text), FollowStore (graph edges), FeedStore (viewer partitions / feed index), ObjectStore (media).
                      </>
                    ),
                    detail:
                      "PostStore can be SQL or a scalable document store; FeedStore often a KV/wide-column optimized for time-ordered scans per viewer.",
                  },
                  {
                    main: (
                      <>
                        <strong>Caching:</strong> Redis caches feed head pages and hydration results (post summaries, author snippets); CDN caches media.
                      </>
                    ),
                    detail:
                      "Cache-aside for reads; write-through for author read-your-writes (author sees post immediately).",
                  },
                  {
                    main: (
                      <>
                        <strong>Async:</strong> PostCreated → Stream (Kafka/PubSub). Fanout workers update followers’ FeedStore (normal authors). For celebrities, store separately and merge at read time.
                      </>
                    ),
                    detail:
                      "Async also updates search indexes and feature/ranking signals. Backpressure is applied via consumer lag and rate limits.",
                  },
                  {
                    main: (
                      <>
                        <strong>Batch/streaming:</strong> Events → feature pipelines → rankScore updates; fallback to recency if ranking pipeline unhealthy.
                      </>
                    ),
                    detail:
                      "ETL into OLAP (analytics) and online features (ranking). Ranking staleness is bounded and treated as a resilience concern.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* In-article ad (optional) */}
          {ADS_ENABLED && (
            <div className="my-4 text-center d-print-none">
              <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" style={{ display: "block", minHeight: 90, textAlign: "center" }} />
            </div>
          )}

          {/* 5 */}
          <Section id="scale" title="5) Scale & resilience plan">
            <Say>“I’ll show sharding/replication, failover, and how we handle pressure and retries.”</Say>

            <Panel title="Checklist" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Sharding:</strong> hash on userId; virtual nodes for smooth reshard; time-bucket partitions.
                      </>
                    ),
                    detail:
                      "Design for skew: separate hot partitions (celebrities), use time buckets, and keep shard moves safe and incremental.",
                  },
                  {
                    main: (
                      <>
                        <strong>Replication:</strong> sync for money/strong consistency; async for read scale; state RPO/RTO and lag budgets.
                      </>
                    ),
                    detail:
                      "Call out what you can tolerate being stale and for how long. Always mention failover and fencing to prevent split-brain writes.",
                  },
                  {
                    main: (
                      <>
                        <strong>Failover:</strong> multi-AZ by default; multi-region read replicas; promotion plan.
                      </>
                    ),
                    detail:
                      "Have a runbook: health signals, who declares incident, promotion steps, DNS/GSLB, rollback strategy.",
                  },
                  {
                    main: (
                      <>
                        <strong>Backpressure:</strong> queue depth thresholds; shed non-critical work; pool caps to protect DB.
                      </>
                    ),
                    detail:
                      "Bound everything: threads, queues, pools. Prefer early 429/503 with Retry-After over cascading failures.",
                  },
                  {
                    main: (
                      <>
                        <strong>Retries:</strong> exponential backoff + jitter; retry budget (e.g., 500ms); idempotency keys.
                      </>
                    ),
                    detail:
                      "Retry only idempotent ops or with idempotency keys. Keep retry budget inside the endpoint SLO; trip breakers on error/latency.",
                  },
                  {
                    main: (
                      <>
                        <strong>Rate limiting:</strong> token bucket at edge per IP/user/app; use Retry-After.
                      </>
                    ),
                    detail:
                      "Use quotas per tenant/user/app; weight expensive endpoints; shape traffic to fragile dependencies with leaky-bucket patterns.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Facebook-like Home Feed — scaling plan" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Sharding:</strong> FeedStore sharded by viewerId (many virtual shards). Celebrity authors handled via separate “celebrity stream” and merged on read.
                      </>
                    ),
                    detail:
                      "This prevents hot keys from exploding write fanout. Time buckets keep partitions manageable and enable fast truncation/TTL.",
                  },
                  {
                    main: (
                      <>
                        <strong>Replication:</strong> Post durability uses stronger writes; FeedStore can be async replicated with bounded lag.
                      </>
                    ),
                    detail:
                      "Target bounded staleness for feed entries (seconds). Use fencing tokens for leader promotion in strong-write stores.",
                  },
                  {
                    main: (
                      <>
                        <strong>Failover:</strong> Multi-AZ for all tiers; optional cross-region warm standby for DR (RTO minutes, RPO seconds–minutes depending on store).
                      </>
                    ),
                    detail:
                      "Start with active-passive DR. Promote read-only region to primary using scripted runbook + DNS/GSLB.",
                  },
                  {
                    main: (
                      <>
                        <strong>Backpressure:</strong> Fanout workers autoscale on lag; if lag spikes, brownout: temporarily reduce ranking complexity and serve recency feed.
                      </>
                    ),
                    detail:
                      "Keep DB protected with pool caps and circuit breakers. Shed optional enrichments first.",
                  },
                  {
                    main: (
                      <>
                        <strong>Retries:</strong> Full jitter retries only for safe reads; post creation uses idempotency key (clientRequestId) to prevent duplicate posts.
                      </>
                    ),
                    detail:
                      "Breakers trip on error rate + p95 latency for downstreams (FeedStore/PostStore). Retries are capped to fit p95 targets.",
                  },
                  {
                    main: (
                      <>
                        <strong>Rate limiting:</strong> Edge token bucket per user/device; stricter on write endpoints and abuse-prone actions (follow/like spam).
                      </>
                    ),
                    detail:
                      "Use 429 + Retry-After; add behavioral signals and step-up checks for suspicious spikes.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* 6 */}
          <Section id="consistency" title="6) Consistency/availability trade-offs & data flows">
            <Say>“For each core flow, I’ll pick a stance on CAP and explain user impact.”</Say>

            <Panel title="Checklist" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Profile updates / money:</strong> prefer C over A → transactional DB; read-your-writes via leader reads.
                      </>
                    ),
                    detail:
                      "Anything security-sensitive or financial favors correctness. Use transactions, explicit locks, and strong read-after-write semantics where needed.",
                  },
                  {
                    main: (
                      <>
                        <strong>Feeds / counts / likes:</strong> prefer A over C → eventual consistency (seconds).
                      </>
                    ),
                    detail:
                      "Users tolerate slight staleness if UX is consistent. Keep staleness bounded and communicate (e.g., optimistic UI).",
                  },
                  {
                    main: (
                      <>
                        <strong>Search/index:</strong> async pipelines; staleness window target (e.g., &lt; 60s).
                      </>
                    ),
                    detail:
                      "Indexes are typically derived. Define SLA for indexing lag and fall back gracefully when index is behind.",
                  },
                  {
                    main: (
                      <>
                        <strong>Techniques:</strong> read-repair; CRDTs for counters/sets; session stickiness or read-my-writes flags; fencing tokens.
                      </>
                    ),
                    detail:
                      "Pick techniques based on conflict risk. Mention how you prevent split-brain writes and how clients get monotonic reads where it matters.",
                  },
                ]}
              />
            </Panel>

            {/* IMPORTANT: Example now follows the SAME template bullets (same order/labels). */}
            <Panel title="Facebook-like Home Feed — CAP stance mapped to the template" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Profile updates / money:</strong> Strong(er). Privacy settings, blocks, and account security must apply immediately and correctly.
                      </>
                    ),
                    detail:
                      "On feed read: enforce privacy/block filters at read time (author visibility), and invalidate cached entries when privacy changes.",
                  },
                  {
                    main: (
                      <>
                        <strong>Feeds / counts / likes:</strong> Eventual. Like/comment counters and “seen” signals can lag by seconds.
                      </>
                    ),
                    detail:
                      "Use optimistic UI and reconcile later. Keep counters in a scalable store (or CRDT-like merges) and accept bounded staleness.",
                  },
                  {
                    main: (
                      <>
                        <strong>Search/index:</strong> Async. Search indexing for posts/users targets staleness &lt; 60s under normal conditions.
                      </>
                    ),
                    detail:
                      "If index lag is high, degrade: show partial results or fall back to recency-only feed generation without search-enriched candidates.",
                  },
                  {
                    main: (
                      <>
                        <strong>Techniques:</strong> Author read-your-writes (write-through cache or leader read); followers see within bounded window via async fanout; fencing tokens on promotions.
                      </>
                    ),
                    detail:
                      "If ranking pipeline is unhealthy, fall back to recency feed to preserve availability and latency SLOs.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* 7 */}
          <Section id="security" title="7) Security, privacy, and compliance">
            <Say>“Security left-to-right: identity → permissions → secrets → encryption → data lifecycle.”</Say>

            <Panel title="Checklist" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>AuthN/AuthZ:</strong> OAuth2/OIDC for users; mTLS/JWT for service-to-service; least-privilege.
                      </>
                    ),
                    detail:
                      "Validate issuer/audience/expiry; prefer short-lived tokens; enforce authorization centrally and at service boundaries.",
                  },
                  {
                    main: (
                      <>
                        <strong>Secrets:</strong> KMS/Vault; rotation; no secrets in images or repos.
                      </>
                    ),
                    detail:
                      "Use dynamic credentials, short TTLs, and audit access. Avoid “secret zero” by using platform identity (IAM/workload identity).",
                  },
                  {
                    main: (
                      <>
                        <strong>Encryption:</strong> TLS 1.2+ in transit; KMS-managed at rest; envelope encryption for PII.
                      </>
                    ),
                    detail:
                      "Encrypt sensitive fields at application level where needed; ensure key usage is auditable and rotation-friendly.",
                  },
                  {
                    main: (
                      <>
                        <strong>Privacy:</strong> minimization, retention &amp; RTBF path; audit logging for access.
                      </>
                    ),
                    detail:
                      "Redact PII from logs/traces; implement deletion events that fan out to caches/search/analytics/backups per policy.",
                  },
                  {
                    main: (
                      <>
                        <strong>Abuse:</strong> WAF, bot detection, CAPTCHA, velocity limits, anomaly scoring.
                      </>
                    ),
                    detail:
                      "Rate-limit writes, detect automation, and apply step-up auth for risky actions. Track abuse metrics and feedback loops.",
                  },
                  {
                    main: (
                      <>
                        <strong>Compliance:</strong> cross-border data? regional stores for residency.
                      </>
                    ),
                    detail:
                      "Have DSAR and breach runbooks. Keep retention schedules documented and enforced via TTL/lifecycle policies.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Facebook-like Home Feed — security & privacy" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>AuthN/AuthZ:</strong> Session cookies for web; short-lived tokens for mobile; service identity via mTLS; strict audience checks on JWTs.
                      </>
                    ),
                    detail:
                      "Authorization checks for visibility happen on feed read (viewer ↔ author relationship, blocks, privacy settings).",
                  },
                  {
                    main: (
                      <>
                        <strong>Secrets:</strong> KMS/Vault; dynamic DB creds for services; automated cert rotation (≤ 90 days).
                      </>
                    ),
                    detail:
                      "No secrets in client apps; all sensitive tokens are HTTP-only cookies or stored securely.",
                  },
                  {
                    main: (
                      <>
                        <strong>Encryption:</strong> TLS everywhere; media URLs are signed; PII fields encrypted with envelope encryption (DEK per record).
                      </>
                    ),
                    detail:
                      "All decrypt operations audited; keys rotated via re-wrap of encrypted DEKs.",
                  },
                  {
                    main: (
                      <>
                        <strong>Privacy:</strong> RTBF triggers deletion event to PostStore, FeedStore, caches, search, analytics; backups expire per policy.
                      </>
                    ),
                    detail:
                      "PII redaction in logs; access logs for sensitive operations with trace IDs.",
                  },
                  {
                    main: (
                      <>
                        <strong>Abuse:</strong> Edge token buckets; spam detection on follows/likes; WAF rules; device reputation; rate-limit suspicious bursts.
                      </>
                    ),
                    detail:
                      "Use gradual friction: throttles → challenges → blocks; keep false-positive appeals path.",
                  },
                  {
                    main: (
                      <>
                        <strong>Compliance:</strong> Region pinning for data residency where required; documented retention for logs and derived features.
                      </>
                    ),
                    detail:
                      "DSAR APIs and runbooks; breach response plan with ownership and timelines.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* 8 */}
          <Section id="observability" title="8) Observability, rollouts, testing">
            <Say>“I’ll make it observable, ship it safely, and prove it holds at peak.”</Say>

            <Panel title="Checklist" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Metrics:</strong> RED for user-facing; USE for infra. Per-endpoint p50/p95/p99; error budgets.
                      </>
                    ),
                    detail:
                      "Treat SLOs as product requirements. Track saturation (queues, pools) to catch incidents before user-facing failures.",
                  },
                  {
                    main: (
                      <>
                        <strong>Logs &amp; traces:</strong> structured logs with correlation/trace IDs; sampling (baseline + 100% on errors).
                      </>
                    ),
                    detail:
                      "Ensure logs are safe (no secrets/PII). Tracing is essential for tail latency and fanout debugging.",
                  },
                  {
                    main: (
                      <>
                        <strong>Dashboards &amp; alerts:</strong> golden signals, queue depth, cache hit ratio, DB replica lag.
                      </>
                    ),
                    detail:
                      "Alert on burn-rate and saturation, not just averages. Include DLQ size and consumer lag for async pipelines.",
                  },
                  {
                    main: (
                      <>
                        <strong>Rollouts:</strong> canary (1%→5%→25%→100%), feature flags, instant rollback.
                      </>
                    ),
                    detail:
                      "Automate rollback based on SLO regression. Keep brownout flags ready to shed expensive work.",
                  },
                  {
                    main: (
                      <>
                        <strong>Testing:</strong> contract tests; load tests to peak ×1.5; chaos (kill a dependency, inject 200ms latency).
                      </>
                    ),
                    detail:
                      "Validate failure modes: breakers, retries with jitter, queue backpressure, and graceful degradation.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Facebook-like Home Feed — observability plan" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Metrics:</strong> FeedRead p95/p99, error rate, saturation; ranking lag; cache hit for feed head; fanout consumer lag.
                      </>
                    ),
                    detail:
                      "SLOs: feed p95 ≤ 200ms; alert on error-budget burn and tail latency spikes during peak windows.",
                  },
                  {
                    main: (
                      <>
                        <strong>Logs &amp; traces:</strong> Trace feed read end-to-end (gateway → feed store → hydration). Sample 100% of slow requests and errors.
                      </>
                    ),
                    detail:
                      "Trace IDs are attached to PostCreated events to debug fanout delays and missing feed entries.",
                  },
                  {
                    main: (
                      <>
                        <strong>Dashboards &amp; alerts:</strong> Consumer lag &gt; 30s, DLQ growth, replica lag, breaker-open rate, throttles (429s).
                      </>
                    ),
                    detail:
                      "If ranking lag breaches threshold, flip feature flag to recency feed and page owners.",
                  },
                  {
                    main: (
                      <>
                        <strong>Rollouts:</strong> Canary new ranking model to 1% → 5% → 25% gated on latency and engagement deltas; rollback on SLO breach.
                      </>
                    ),
                    detail:
                      "Feature flags allow immediate brownout (disable expensive enrichments) without redeploy.",
                  },
                  {
                    main: (
                      <>
                        <strong>Testing:</strong> Load test feed at peak, chaos inject 200–500ms to FeedStore and drop one AZ; verify fallback to cache/recency.
                      </>
                    ),
                    detail:
                      "Contract tests for event schemas (PostCreated/FollowChanged) to keep producers/consumers deployable independently.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* 9 */}
          <Section id="tradeoffs" title="9) Explicit trade-offs & alternatives">
            <Say>“Here are the options I considered, what I chose, and when I’d switch.”</Say>

            <Panel title="Checklist" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Fan-out on write vs fan-in on read (feeds):</strong> choose fan-out for small/medium creators; mix models for heavy skew.
                      </>
                    ),
                    detail:
                      "Pure fanout explodes writes for celebrities; pure fan-in increases read-time latency and compute. Hybrid is common.",
                  },
                  {
                    main: (
                      <>
                        <strong>DB choice:</strong> doc/KV for feed + relational for accounts/payments; consider wide-column for huge time-series partitions.
                      </>
                    ),
                    detail:
                      "Match store to access patterns and failure modes. Keep transactional boundaries clear and avoid cross-shard transactions.",
                  },
                  {
                    main: (
                      <>
                        <strong>Multi-region:</strong> start single-region write + global CDN + async read replicas; evolve to active-active/CRDTs when global write latency matters.
                      </>
                    ),
                    detail:
                      "Multi-region adds complexity. Justify it with business needs (latency, regulatory, DR).",
                  },
                  {
                    main: (
                      <>
                        <strong>Consistency:</strong> eventual for counts/feeds, strong for money/profile; session consistency for UX without full strong.
                      </>
                    ),
                    detail:
                      "Be explicit about user-visible effects and how you mitigate them (optimistic UI, bounded staleness, read-your-writes).",
                  },
                ]}
              />
            </Panel>

            <Panel title="Facebook-like Home Feed — trade-offs recap" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Fan-out vs fan-in:</strong> Hybrid: fan-out for normal users, fan-in merge for celebrities.
                      </>
                    ),
                    detail:
                      "Keeps read latency low for most users while preventing write amplification from extreme fanout.",
                  },
                  {
                    main: (
                      <>
                        <strong>DB choice:</strong> Strong store for posts/privacy; scalable feed index store for viewer partitions; object store + CDN for media.
                      </>
                    ),
                    detail:
                      "Feed index is derived; posts are source of truth. Hydration is batched to avoid N+1 calls.",
                  },
                  {
                    main: (
                      <>
                        <strong>Multi-region:</strong> Single write region + Multi-AZ initially; global edge + CDN; warm standby DR.
                      </>
                    ),
                    detail:
                      "Evolve to multi-region reads/active-active only if business needs it (global write latency, regulatory).",
                  },
                  {
                    main: (
                      <>
                        <strong>Consistency:</strong> Strong for privacy/block/durability; eventual for counters/ranking/search with bounded staleness.
                      </>
                    ),
                    detail:
                      "Protect UX with read-your-writes for author and fallback to recency during ranking outages.",
                  },
                ]}
              />
            </Panel>
          </Section>
        </main>

        {/* RIGHT: Sponsored */}
        <aside className="d-none d-lg-block col-lg-4 col-xl-3 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <RightSponsoredCard />
          </div>
        </aside>
      </div>
    </div>
  );
}
